/** Zapytania modułu `timesheet` (TanStack Query v5). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@/lib/api'
import { api } from '@/lib/api'
import type { Paginated } from '@/lib/types'
import type {
  CaseOption,
  TimeEntry,
  TimeEntryCreatePayload,
  TimeEntryListResponse,
  TimeEntryUpdatePayload,
  TimesheetStats,
} from './types'

export const timesheetKeys = {
  all: ['timesheet'] as const,
  list: (params: Query) => ['timesheet', 'list', params] as const,
  stats: ['timesheet', 'stats'] as const,
  cases: ['timesheet', 'cases'] as const,
}

export interface TimeEntryFilters {
  page?: number
  size?: number
  case_id?: number
  user_id?: number
  from?: string
  to?: string
  billable?: boolean
}

/** Lista wpisów (ekran „Czas pracy" filtruje po `user_id` zalogowanego — „moje wpisy"). */
export function useTimeEntries(filters: TimeEntryFilters = {}) {
  const query: Query = { size: 50, ...filters }
  return useQuery({
    queryKey: timesheetKeys.list(query),
    queryFn: () => api.get<TimeEntryListResponse>('/timesheet', { query }),
    placeholderData: (previous) => previous,
  })
}

/** KPI nagłówka strony i kafla pulpitu — godziny bieżącego użytkownika. */
export function useTimesheetStats() {
  return useQuery({
    queryKey: timesheetKeys.stats,
    queryFn: () => api.get<TimesheetStats>('/timesheet/stats'),
    staleTime: 60_000,
  })
}

/** Sprawy do wyboru w formularzu wpisu — endpoint modułu `cases` (zawsze włączony). */
export function useCaseOptions(enabled = true) {
  return useQuery({
    queryKey: timesheetKeys.cases,
    queryFn: () => api.get<Paginated<CaseOption>>('/cases', { query: { size: 200 } }),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: TimeEntryCreatePayload) => api.post<TimeEntry>('/timesheet', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all })
    },
  })
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: TimeEntryUpdatePayload & { id: number }) =>
      api.patch<TimeEntry>(`/timesheet/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all })
    },
  })
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/timesheet/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all })
    },
  })
}
